import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getSICReportStatus,
    getSICReportHistory,
    getSICReportAttention,
    runSICReportManual,
} from '@/services/ops.service';
import { PARTNER_ID } from '@/constants';
import type {
    SICReportStatus,
    SICReportHistoryItem,
    SICAttentionItem,
    SICRunRequest,
    SICRunResult,
} from '@/types/sic-report.types';
import { MOCK_STATUS, MOCK_HISTORY, MOCK_ATTENTION } from '../mock/sic-mock-data';

const USE_MOCK = false;

const STATUS_KEY = (partnerId: string) => ['sic-status', partnerId];
const HISTORY_KEY = (partnerId: string) => ['sic-history', partnerId];
const ATTENTION_KEY = (partnerId: string) => ['sic-attention', partnerId];

export function useSICReport() {
    const queryClient = useQueryClient();

    const statusQuery = useQuery({
        queryKey: STATUS_KEY(PARTNER_ID),
        queryFn: async (): Promise<SICReportStatus | null> => {
            if (USE_MOCK) {
                await new Promise((r) => setTimeout(r, 400));
                return MOCK_STATUS;
            }
            const res = await getSICReportStatus(PARTNER_ID);
            return res.data;
        },
        staleTime: 60_000,
    });

    const historyQuery = useQuery({
        queryKey: HISTORY_KEY(PARTNER_ID),
        queryFn: async (): Promise<SICReportHistoryItem[]> => {
            if (USE_MOCK) {
                await new Promise((r) => setTimeout(r, 600));
                return MOCK_HISTORY;
            }
            const res = await getSICReportHistory(PARTNER_ID);
            return res.data ?? [];
        },
        staleTime: 5 * 60_000,
    });

    const attentionQuery = useQuery({
        queryKey: ATTENTION_KEY(PARTNER_ID),
        queryFn: async (): Promise<SICAttentionItem[]> => {
            if (USE_MOCK) {
                await new Promise((r) => setTimeout(r, 500));
                return MOCK_ATTENTION;
            }
            const res = await getSICReportAttention(PARTNER_ID);
            return res.data ?? [];
        },
        staleTime: 5 * 60_000,
    });

    const runMutation = useMutation({
        mutationFn: async (input: Omit<SICRunRequest, 'partnerId'>): Promise<SICRunResult> => {
            if (USE_MOCK) {
                await new Promise((r) => setTimeout(r, 1200));
                return {
                    reportId: 'mock-manual-' + Date.now(),
                    partnerId: PARTNER_ID,
                    runDate: input.runDate ?? new Date().toISOString().split('T')[0] ?? '',
                    status: 'COMPLETED',
                    eligible: 5, skipped: 3, sent: 2, confirmed: 2, rejected: 0, error: 0,
                };
            }
            const res = await runSICReportManual({ partnerId: PARTNER_ID, ...input });
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: STATUS_KEY(PARTNER_ID) });
            queryClient.invalidateQueries({ queryKey: HISTORY_KEY(PARTNER_ID) });
            queryClient.invalidateQueries({ queryKey: ATTENTION_KEY(PARTNER_ID) });
        },
    });

    return {
        partnerId: PARTNER_ID,
        status: statusQuery.data ?? null,
        statusLoading: statusQuery.isLoading,
        history: historyQuery.data ?? [],
        historyLoading: historyQuery.isLoading,
        attention: attentionQuery.data ?? [],
        attentionLoading: attentionQuery.isLoading,
        runManual: runMutation.mutate,
        isRunning: runMutation.isPending,
        runResult: runMutation.data,
        runError: runMutation.error,
    };
}
